import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { toZonedTime } from 'date-fns-tz';

import { Chat } from '../Chat/Chat';
import { EnterYourData } from './EnterYourData';
import { LeaveDialogs } from './LeaveDialogs';
import { ChatEnterButton } from './ChatEnterButton';
import MainStyled from './MainStyled';

import { getDayName } from '../../utils';
import { loadGoogleFont } from '../../utils/loadGoogleFont';
import { useIframePositioning } from '../../utils/useIframePositioning';

import { useChatLifecycle } from '../../hooks/useChatLifecycle';
import { useDialogSocketHandlers } from '../../hooks/useDialogSocketHandlers';
import { useResponsive } from '../../hooks/useResponsive';

import { useChat } from '../../context/Context';

export const Main = () => {
    const {
        dialogId,
        isDialogActive,
        setDialogActive,
        messages,
        socketInstance,
        widgetData,
        showWidget,
    } = useChat();

    const {
        font: currentFont,
        timeout,
        position,
        rounding,
        margins = [10, 10],
        timezone,
        schedule,
        channels,
        hide_copy,
        visitor_data_request,
    } = widgetData || {};

    const [isWidgetShown, setWidgetShown] = useState(true);
    const [isLeaveDialogBlockShown, setLeaveDialogBlockShown] = useState(false);
    const [isRequestpersonalDataShown, setRequestpersonalDataShown] = useState(false);
    const [isAddYourDataVisible, setIsAddYourDataVisible] = useState(false);
    const [isTimeout, setIsTimeout] = useState(false);
    const [isOnline, setIsOnline] = useState(false);
    const [isChatActive] = useState(false);

    const manualToggleRef = useRef(false);
    const isSmallScreen = useResponsive();

    const {
        fetchDialogId,
        setDialogData,
        isPreload,
        setPreload,
        preloadMessagesHandler,
        sendMessage,
        messagesRequest,
        isLoading,
        messagesData,
        closeDialog,
    } = useChatLifecycle();

    useEffect(() => {
        if (dialogId?.data?.id && isDialogActive) {
            messagesRequest();
        }
    }, [dialogId?.data?.id, isDialogActive]);

    useDialogSocketHandlers({
        socketInstance,
        dialogId,
        fetchDialogId,
        messagesRequest,
        setDialogActive,
        setIsAddYourDataVisible,
        setData: setDialogData,
        setWidgetShown,
        setIsOnline,
    });

    useEffect(() => {
        if (!isWidgetShown && isDialogActive && !manualToggleRef.current) {
            setWidgetShown(true);
        }
        manualToggleRef.current = false;
    }, [isDialogActive]);

    useEffect(() => {
        if (currentFont) loadGoogleFont(currentFont);
    }, [currentFont]);

    useEffect(() => {
        if (!timeout) {
            setIsTimeout(true);
            return;
        }
        const timer = setTimeout(() => setIsTimeout(true), timeout * 1000);
        return () => clearTimeout(timer);
    }, [timeout]);

    useIframePositioning({
        isWidgetShown,
        isSmallScreen,
        showWidget,
        position,
        radius: rounding?.window,
        margins,
    });

    const isInSchedule = useMemo(() => {
        const currentDate = new Date();
        const zonedDate = toZonedTime(currentDate, timezone);
        const scheduleDay = getDayName(currentDate);
        const daySchedule = schedule?.[scheduleDay];

        if (!daySchedule?.active) return false;

        const start = new Date(`${zonedDate.toDateString()} ${daySchedule.start}`);
        const end = new Date(`${zonedDate.toDateString()} ${daySchedule.end}`);

        return start < zonedDate && end > zonedDate;
    }, [schedule, timezone]);

    const hideYourDataHandler = useCallback(() => {
        setIsAddYourDataVisible(prev => !prev);
        setRequestpersonalDataShown(prev => !prev);
    }, []);

    const closeDialogHandler = useCallback(() => {
        closeDialog({ method: 'POST' }).then(() => {
            messagesRequest();
            setDialogActive(false);
            setLeaveDialogBlockShown(false);
            localStorage.setItem('dialogStatus', 'closed');
        });
    }, [closeDialog, messagesRequest]);

    const toggleChat = useCallback(() => {
        if (isPreload) setPreload(false);
        manualToggleRef.current = true;
        setWidgetShown(prev => !prev);
    }, [isPreload]);

    const membersData = useMemo(() => {
        return messagesData?.data?.status === 'unresolved'
            ? messagesData?.data?.chat?.members
            : [];
    }, [messagesData]);

    if (!isTimeout) return null;

    return !isWidgetShown ? (
        <MainStyled
            isRequestpersonalDataShown={isRequestpersonalDataShown}
            isLeaveDialogBlockShown={isLeaveDialogBlockShown}
            isSmallScreen={isSmallScreen}
            position={position}
            margins={margins}
            disableGutters
        >
            {isLeaveDialogBlockShown ? (
                <LeaveDialogs
                    showChatHandler={() => setLeaveDialogBlockShown(false)}
                    hideChatHandler={closeDialogHandler}
                />
            ) : !isRequestpersonalDataShown ? (
                <Chat
                    messagesPreload={preloadMessagesHandler}
                    messagesLoading={isLoading}
                    dialogId={dialogId?.data?.id}
                    hasPreloadedMessages={messagesData?.data?.preload && !isPreload}
                    showRequest={isAddYourDataVisible}
                    isInSchedule={isInSchedule}
                    isOnline={isOnline}
                    isDialogActive={isDialogActive}
                    messages={messages}
                    members={membersData}
                    channels={channels}
                    isChatActive={isChatActive}
                    sendMessage={sendMessage}
                    sendMessageWithActiveDialog={(fn) => fn(dialogId)}
                    onClick={hideYourDataHandler}
                    onClose={toggleChat}
                    onCancel={() => setLeaveDialogBlockShown(true)}
                    onShowRequest={() => setRequestpersonalDataShown(true)}
                />
            ) : (
                <EnterYourData
                    dialogId={dialogId?.data?.id}
                    id={dialogId?.data?.id}
                    fields={visitor_data_request}
                    onClose={hideYourDataHandler}
                />
            )}
        </MainStyled>
    ) : (
        <ChatEnterButton onClick={toggleChat} messagesAmont={1} />
    );
};
